import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Tag } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import SEO from "../components/SEO";
import CalculatorCallout from "../components/CalculatorCallout";
import { blogPosts } from "../data/blog";
import { localBusinessSchema, breadcrumbSchema } from "../utils/schemas";

const BlogIndexPage = () => {
  const [featured, ...rest] = blogPosts;

  const schemas = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      url: "https://claimbeast.com/blog",
      name: "Claim Beast Blog",
      description:
        "Florida insurance claim help, hurricane prep, and public adjuster guidance from Joe and Victoria at Claim Beast.",
      blogPost: blogPosts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://claimbeast.com/blog/${p.slug}`,
        datePublished: p.date,
        author: { "@type": "Person", name: p.author },
      })),
    },
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title="Blog | Florida Insurance Claim Tips & Hurricane Guides | Claim Beast"
        description="Free guides from Tampa Bay public adjusters Joe and Victoria. Hurricane damage documentation, denied claim help, AOB warnings, and Florida insurance tips."
        path="/blog"
        keywords="florida insurance blog, hurricane damage guide tampa, public adjuster blog, denied claim tips florida, claim beast blog"
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={[{ name: "Blog", path: "/blog" }]} />

      {/* Hero */}
      <section className="relative bg-black py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_40%,rgba(225,29,38,0.12),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-[#E11D26]" />
            <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
              Insights From The Field
            </span>
          </div>
          <h1
            className="text-white text-5xl sm:text-6xl md:text-7xl font-black uppercase leading-[0.92]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            The <span className="text-[#E11D26]">Claim Beast</span> Blog.
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl">
            Hurricane prep, denied claim playbooks, and Florida insurance
            tactics — written by Joe &amp; Victoria from the front lines of
            Tampa Bay claims.
          </p>
        </div>
      </section>

      {/* Featured post */}
      <section className="bg-[#0A0A0A] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Link
            to={`/blog/${featured.slug}`}
            className="group grid lg:grid-cols-12 gap-8 items-center bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26]/50 p-6 sm:p-8 transition-colors"
          >
            <div className="lg:col-span-6 relative overflow-hidden">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 bg-[#E11D26] text-white text-xs font-bold tracking-[0.2em] uppercase px-3 py-1.5">
                Featured
              </span>
            </div>
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 text-xs text-white/55">
                <span className="flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5 text-[#E11D26]" />
                  {featured.category}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {featured.readTime}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(featured.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
              <h2
                className="mt-3 text-white text-3xl sm:text-4xl font-black uppercase leading-tight group-hover:text-[#E11D26] transition-colors"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                {featured.title}
              </h2>
              <p className="mt-4 text-white/70 leading-relaxed">
                {featured.excerpt}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-[#E11D26] font-bold tracking-wider uppercase text-sm">
                Read Article
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid of remaining posts */}
      <section className="bg-black py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl font-black text-white uppercase mb-7"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            More <span className="text-[#E11D26]">Articles</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group flex flex-col bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26]/50 transition-colors overflow-hidden"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 bg-[#E11D26] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1">
                    {post.category}
                  </span>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[11px] text-white/55">
                    <span>{post.readTime}</span>
                    <span>·</span>
                    <span>
                      {new Date(post.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <h3
                    className="mt-2 text-white text-xl font-black uppercase leading-tight group-hover:text-[#E11D26] transition-colors"
                    style={{
                      fontFamily: "'Anton', 'Oswald', sans-serif",
                    }}
                  >
                    {post.title}
                  </h3>
                  <p className="mt-2 text-white/65 text-sm leading-relaxed line-clamp-3 flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 text-[#E11D26] font-bold tracking-wider uppercase text-xs">
                    Read More
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CalculatorCallout
        eyebrow="Skip Ahead — Get The Number"
        headline="Reading Won't Recover Your Money. Math Will."
        body="Our blog explains the playbook. The calculator runs it on your specific damage. Walk through a few questions and we'll show you what a properly documented claim should be worth — using current Florida market rates."
        ctaLabel="Calculate My Claim"
      />

      <Footer />
    </div>
  );
};

export default BlogIndexPage;
